/**
 * Why is this workload not usable for a training yet? — NC-1296 Soll 4.
 *
 * The dead-end class this closes is #1273: a workload could be created, listed
 * and even picked in the run dialog while the run-lifecycle refused it — the
 * user only found out from a 400 after filling in a whole form. The fix there
 * was to stop offering it; the fix here is to say **why**, on the workload
 * itself, before anyone tries.
 *
 * Deliberately pure and request-free: every signal already sits on the workload
 * document the list page has fetched (`isProvisionable` from NC-1273, the
 * packerConfig/applications fields via `evaluateBuildReadiness`) or in the
 * capability lists. That keeps the list page at one request and makes each rule
 * unit-testable.
 *
 * What this does NOT check, on purpose: whether a **published image actually
 * exists** right now. That is live per-workload data (the images list), and
 * fetching it per card would turn one request into N. `isStagingOnly` covers the
 * structural half of that concern — a workload whose images can never be
 * promoted will never have a production image — while the live half stays where
 * it is already visible, on the Image-Builds page.
 */

import { WorkloadDefinition } from '../services/types';
import { isStagingOnly } from '../config/workloadCapabilities';
import { evaluateBuildReadiness } from './workloadReadiness';

export type TrainabilityStatus = 'ready' | 'blocked' | 'incomplete';

export interface TrainabilityReason {
  key: string;
  /** What is wrong, in the operator's terms. */
  text: string;
  /** What to do about it, when there is something actionable. */
  hint?: string;
}

export interface TrainabilityResult {
  status: TrainabilityStatus;
  /** Short chip label. */
  label: string;
  /**
   * Everything standing in the way, most fundamental first. Empty iff
   * `status === 'ready'`.
   */
  reasons: TrainabilityReason[];
  /** true when at least one reason cannot be fixed by editing the workload. */
  hasHardBlocker: boolean;
}

/**
 * Evaluate whether a workload can carry a training.
 *
 * Precedence matters: a hard blocker is reported even when the configuration is
 * also incomplete, because completing the form would not help.
 */
export function evaluateTrainability(workload: WorkloadDefinition): TrainabilityResult {
  const reasons: TrainabilityReason[] = [];
  let hasHardBlocker = false;

  if (workload.isActive === false) {
    hasHardBlocker = true;
    reasons.push({
      key: 'inactive',
      text: 'Workload ist inaktiv und kann nicht für Schulungen gewählt werden.',
      hint: 'Im Editor auf „Aktiv" stellen.',
    });
  }

  // NC-1273 contract: only an explicit `false` blocks. `undefined` means an
  // older API response, and treating that as "not provisionable" would mark
  // every workload unusable on a stale deployment.
  if (workload.isProvisionable === false) {
    hasHardBlocker = true;
    reasons.push({
      key: 'not-provisionable',
      text: 'Die Bereitstellung akzeptiert diesen Workload nicht — ein Lauf würde abgewiesen.',
      hint: 'Freischaltung erfolgt in der Bereitstellungs-Pipeline, nicht im Editor.',
    });
  }

  if (isStagingOnly(workload.id)) {
    hasHardBlocker = true;
    reasons.push({
      key: 'staging-only',
      text: 'Images dieses Workloads bleiben in Staging und werden nie nach Produktion übernommen.',
      hint: 'Bewusste Festlegung (NC-984) — kein Editor-Problem.',
    });
  }

  const readiness = evaluateBuildReadiness({
    applicationsCount: workload.applications?.length ?? 0,
    baseImage: workload.packerConfig?.baseImage ?? '',
    templateFile: workload.packerConfig?.templateFile ?? '',
    variablesFile: workload.packerConfig?.variablesFile ?? '',
  });

  for (const check of readiness.checks) {
    if (!check.ok) {
      reasons.push({ key: `readiness-${check.key}`, text: check.label, hint: check.hint });
    }
  }

  if (reasons.length === 0) {
    return { status: 'ready', label: 'Schulbar', reasons: [], hasHardBlocker: false };
  }

  return {
    status: hasHardBlocker ? 'blocked' : 'incomplete',
    label: hasHardBlocker ? 'Nicht schulbar' : 'Unvollständig',
    reasons,
    hasHardBlocker,
  };
}
